import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAuthForLink from '../../customHooks/useAuthForLink';
import newRequestServices from '../../common/newRequestServices';
import { isServiceSupported, loadBookingContext } from '../../common/bookingContext';
import { isWithinServingHours, loadHotel } from '../../common/servingHours';
import OutsideServingHours from '../components/ServingHours/OutsideServingHours';
import PageLoader from '../components/PageLoader/PageLoader';

// Raising a new request: /bookings/:bookingId/requests/new/:service
//
// The request forms all read their booking details from location.state, so this
// resolves the booking from the URL and hands them the same shape they used to
// get from HotelView. That keeps every form addressable and refresh-safe
// without rewriting fourteen screens.
export default function NewRequest() {

    const { bookingId, service } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const isAuthenticated = useAuthForLink(location.pathname);

    const [isLoading, setIsLoading] = useState(true);
    const [Component, setComponent] = useState(null);
    const [closedHotel, setClosedHotel] = useState(null);

    const isReady = Component && location.state?.__fromNewRequest;

    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }

        let isCancelled = false;

        const resolveService = async () => {
            try {
                setIsLoading(true);

                // location.state still describes the previous service, so drop
                // the old form before resolving a different one.
                setComponent(null);
                setClosedHotel(null);

                const entry = newRequestServices[service];

                if (!entry) {
                    navigate(`/bookings/${bookingId}`, { replace: true });
                    return;
                }

                const context = await loadBookingContext(bookingId);

                if (isCancelled) {
                    return;
                }

                if (!context.hotelId) {
                    navigate("/bookings", { replace: true });
                    return;
                }

                // Both only need the hotel, so the support check and the
                // screen's own data are fetched together rather than one after
                // the other. The load is wasted when the hotel turns out not to
                // offer the service, which is the rare path.
                const [supported, hotel, extra] = await Promise.all([
                    isServiceSupported(context.hotelId, entry.serviceName),
                    loadHotel(context.hotelId),
                    entry.load(context),
                ]);

                if (isCancelled) {
                    return;
                }

                // Outside the hotel's serving hours nobody is on the other end
                // of the request, so the guest is pointed at reception instead.
                // Web check-in never lands here, it has its own route.
                if (!isWithinServingHours(hotel?.service_start_time, hotel?.service_end_time)) {
                    setClosedHotel(hotel);
                    return;
                }

                if (!supported) {
                    toast.error(`Your Hotel Does Not Support ${entry.serviceName} Service`);
                    navigate(`/bookings/${bookingId}`, { replace: true });
                    return;
                }

                setComponent(() => entry.Component);

                navigate(location.pathname, {
                    state: {
                        ...context,
                        ...extra,

                        // Anything the caller passed wins, so stepping back from
                        // a summary screen keeps the guest's cart instead of
                        // resetting it to a freshly fetched menu.
                        ...(location.state || {}),

                        activeTab: location.state?.activeTab || "",
                        __fromNewRequest: true,
                    },
                    replace: true,
                });

            } catch (error) {
                console.log(error);
                if (!isCancelled) {
                    toast.error(error?.response?.data?.message || "Some Error Occured");
                    navigate(`/bookings/${bookingId}`, { replace: true });
                }
            }
            finally {
                if (!isCancelled) {
                    setIsLoading(false);
                }
            }
        };

        resolveService();

        return () => {
            isCancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, bookingId, service]);

    if (closedHotel) {
        return <OutsideServingHours hotel={closedHotel} bookingId={bookingId} />;
    }

    return (
        <>
            {isLoading && <PageLoader />}
            {isReady && <Component />}
        </>
    );
}
